import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Link, useSearchParams } from 'react-router-dom';
import { ciclesService, modulsService, Cicle, Modul } from '../services/api';
import ModulModal from '../components/ModulModal';
import ConfirmDialog from '../components/ConfirmDialog';

function ModulsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cicleInicial = Number(searchParams.get('cicle'));
  const [cicles, setCicles] = useState<Cicle[]>([]);
  const [cicleSeleccionat, setCicleSeleccionat] = useState<number | null>(
    Number.isInteger(cicleInicial) && cicleInicial > 0 ? cicleInicial : null
  );
  const [moduls, setModuls] = useState<Modul[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingModuls, setLoadingModuls] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modulToEdit, setModulToEdit] = useState<Modul | null>(null);
  const [modulToDelete, setModulToDelete] = useState<Modul | null>(null);

  // Carregar llista de cicles
  const carregarCicles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ciclesService.getAll();
      setCicles(data.filter(c => c.actiu));
    } catch (error) {
      console.error('Error carregant cicles:', error);
      alert('Error en carregar els cicles formatius.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar mòduls del cicle seleccionat
  const carregarModuls = useCallback(async () => {
    if (cicleSeleccionat === null) return;
    try {
      setLoadingModuls(true);
      const data = await modulsService.getAll(cicleSeleccionat);
      setModuls(data);
    } catch (error) {
      console.error('Error carregant mòduls:', error);
      alert('Error en carregar els mòduls professionals.');
    } finally {
      setLoadingModuls(false);
    }
  }, [cicleSeleccionat]);

  useEffect(() => {
    carregarCicles();
  }, [carregarCicles]);

  useEffect(() => {
    carregarModuls();
  }, [carregarModuls]);

  const handleCreate = () => {
    setModulToEdit(null);
    setModalOpen(true);
  };

  const handleCicleChange = (valor: string) => {
    const cicleId = valor ? Number(valor) : null;
    setCicleSeleccionat(cicleId);
    setSearchParams(cicleId ? { cicle: String(cicleId) } : {});
  };

  const handleEdit = (modul: Modul) => {
    setModulToEdit(modul);
    setModalOpen(true);
  };

  const handleDeleteClick = (modul: Modul) => {
    setModulToDelete(modul);
  };

  const handleSave = async (data: { codi: string; nom: string; hores: number; curs: number }) => {
    if (cicleSeleccionat === null) return;

    try {
      if (modulToEdit) {
        await modulsService.update(cicleSeleccionat, modulToEdit.id, data);
      } else {
        await modulsService.create(cicleSeleccionat, data);
      }
      setModalOpen(false);
      setModulToEdit(null);
      await carregarModuls();
    } catch (error) {
      console.error('Error guardant mòdul:', error);
      alert('Error en guardar el mòdul professional.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!modulToDelete || cicleSeleccionat === null) return;

    try {
      await modulsService.delete(cicleSeleccionat, modulToDelete.id);
      setModulToDelete(null);
      await carregarModuls();
    } catch (error) {
      console.error('Error eliminant mòdul:', error);
      alert('Error en eliminar el mòdul professional.');
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || cicleSeleccionat === null) return;

    const items = Array.from(moduls);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setModuls(items);

    try {
      await modulsService.reorder(cicleSeleccionat, items);
    } catch (error) {
      console.error('Error reordenant mòduls:', error);
      await carregarModuls();
    }
  };

  if (loading) {
    return <div className="page-container"><div className="loading">Carregant...</div></div>;
  }

  const cicleActual = cicles.find(c => c.id === cicleSeleccionat);

  return (
    <div className="page-container">
      {/* Selector de Cicle Formatiu */}
      <div className="selector-container">
        <div className="selector-header">
          <h2>Mòduls Professionals</h2>
        </div>
        <div className="selector-body">
          <label htmlFor="cicle-select" className="selector-label">Selecciona un Cicle Formatiu:</label>
          <select
            id="cicle-select"
            className="selector-select"
            value={cicleSeleccionat ?? ''}
            onChange={(e) => handleCicleChange(e.target.value)}
          >
            <option value="">-- Selecciona un cicle --</option>
            {cicles.map((cicle) => (
              <option key={cicle.id} value={cicle.id}>
                {cicle.nom} - {cicle.descripcio}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Llista de mòduls */}
      {cicleSeleccionat !== null && (
        <div className="table-container" style={{ marginTop: 24 }}>
          <div className="table-header">
            <h2>
              {cicleActual ? `Mòduls de ${cicleActual.nom}` : 'Mòduls'}
              {loadingModuls && <span className="loading-spinner"> ⟳</span>}
            </h2>
            <button className="btn btn-primary" onClick={handleCreate}>
              + Nou mòdul
            </button>
          </div>

          {!loadingModuls && moduls.length === 0 ? (
            <div className="empty-state">
              <p>Aquest cicle no té mòduls professionals. Crea'n un de nou!</p>
            </div>
          ) : loadingModuls ? (
            <div className="empty-state">
              <p>Carregant mòduls...</p>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="moduls-list">
                {(provided) => (
                  <table ref={provided.innerRef} {...provided.droppableProps}>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th>
                        <th>Codi</th>
                        <th>Nom del mòdul</th>
                        <th>Hores</th>
                        <th>Accions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moduls.map((modul, index) => (
                        <Draggable key={modul.id} draggableId={String(modul.id)} index={index}>
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={snapshot.isDragging ? 'dragging' : ''}
                            >
                              <td {...provided.dragHandleProps} className="drag-handle-cell">
                                <span className="drag-handle">⠿</span>
                              </td>
                              <td>
                                <Link
                                  className="modul-link"
                                  to={`/moduls/${cicleSeleccionat}/${modul.id}`}
                                >
                                  <code className="modul-codi">{modul.codi}</code>
                                </Link>
                              </td>
                              <td>
                                <Link
                                  className="modul-link modul-name-link"
                                  to={`/moduls/${cicleSeleccionat}/${modul.id}`}
                                >
                                  {modul.nom}
                                </Link>
                              </td>
                              <td><span className="modul-hores">{modul.hores} h</span></td>
                              <td>
                                <div className="actions-cell">
                                  <button
                                    className="btn-icon edit"
                                    title="Editar"
                                    onClick={() => handleEdit(modul)}
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    className="btn-icon delete"
                                    title="Eliminar"
                                    onClick={() => handleDeleteClick(modul)}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  </table>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      )}
      
      {cicleSeleccionat === null && !loading && (
        <div className="table-container" style={{ marginTop: 24 }}>
          <div className="empty-state">
            <p>Selecciona un cicle formatiu per veure els seus mòduls professionals.</p>
          </div>
        </div>
      )}

      <ModulModal
        isOpen={modalOpen}
        modul={modulToEdit}
        onClose={() => { setModalOpen(false); setModulToEdit(null); }}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={!!modulToDelete}
        title="Eliminar mòdul"
        message={`Estàs segur que vols eliminar el mòdul "${modulToDelete?.nom}" (${modulToDelete?.codi})? Aquesta acció no es pot desfer.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setModulToDelete(null)}
      />
    </div>
  );
}

export default ModulsPage;
